import * as React from "react";
import { Alert, AlertTitle, Box, Button, Link, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAcknowledgeLegal } from "@/api/legal";
import type { LegalNotice } from "@/api/types";
import { changesFrom, isLegalDoc, legalChangesPath, legalDocLabel } from "@/features/legal/docs";
import { changeSummary, legalDay } from "@/features/legal/orgTermsText";
import { formatDate } from "@/i18n/format";

/** Information notices (spec §9.4), all in one: a headline, one line
 *  per document ("We updated <document> on <date>." with a short
 *  summary, "Read what changed" and "Read the new version") and one
 *  "Got it", which acknowledges every version shown. Never blocking;
 *  never hashes or internal names. */
export function LegalUpdateNotice({ notices }: { notices: LegalNotice[] }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const ack = useAcknowledgeLegal();
  if (notices.length === 0) return null;
  return (
    <Alert
      severity="info"
      data-testid="legal-update-notice"
      // On a phone the actions go under the text instead of squeezing it.
      sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, "& .MuiAlert-message": { flex: "1 1 220px" } }}
      action={
        <Button
          color="inherit"
          size="small"
          disabled={ack.isPending}
          onClick={() =>
            ack.mutate({
              documents: Object.fromEntries(notices.map((n) => [n.doc, n.version])),
              ui_language: i18n.resolvedLanguage ?? i18n.language,
            })
          }
        >
          {t("whats_new.got_it")}
        </Button>
      }
    >
      <AlertTitle>{t("legal.update.headline", { count: notices.length })}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
        {notices.map((n) => (
          <NoticeLine key={`${n.doc}-${n.version}`} notice={n} />
        ))}
      </Box>
    </Alert>
  );
}

function NoticeLine({ notice }: { notice: LegalNotice }): React.JSX.Element {
  const { t } = useTranslation();
  const doc = isLegalDoc(notice.doc) ? legalDocLabel(t, notice.doc) : notice.doc;
  // The publication date in Sweden; an older backend sends only the timestamp.
  const date = notice.published ? legalDay(notice.published) : formatDate(notice.published_at);
  // "What changed": from the version the user last accepted or saw.
  const from = notice.summary_key === "new_document" ? null : changesFrom(notice.seen_version, notice.version);
  return (
    <Box component="li" data-testid="legal-update-line">
      <Stack sx={{ gap: 0.25 }}>
        <span>
          {notice.summary_key === "new_document"
            ? t("legal.update.body_new", { doc, date })
            : `${t("legal.update.body", { doc, date })} ${changeSummary(t, notice.summary_key)}`}
        </span>
        <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
          {from && isLegalDoc(notice.doc) && (
            <Link
              href={legalChangesPath(notice.doc, from, notice.version)}
              target="_blank"
              rel="noopener"
              color="inherit"
              data-testid="legal-update-what-changed"
              // Every line has the same two links: the name says which document.
              aria-label={t("legal.update.read_label", { doc })}
            >
              {t("legal.update.read")}
            </Link>
          )}
          <Link
            href={`/legal/${notice.doc}?version=${notice.version}`}
            target="_blank"
            rel="noopener"
            color="inherit"
            aria-label={t("legal.update.read_document_label", { doc })}
          >
            {t("legal.update.read_document")}
          </Link>
        </Stack>
      </Stack>
    </Box>
  );
}
